import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Chip,
  Collapse,
  Skeleton,
} from '@billda/design-system';
import { ExpandMoreIcon, GroupsIcon, PersonIcon } from '@billda/design-system';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import type { UnapprovedAllocationDto } from '@billda/api';
import { routeBuilders } from '../../../routes';
import { formatCurrency } from '../../../shared/utils/currency';
import { ChipTabs, type ChipTab } from '../../../shared/components/ui/ChipTabs';
import { TreeConnectorBox } from '../../../shared/components/grouped-view';
import { PotStatusBadge } from '../../finance/components/PotStatusBadge';
import { useGetUnapprovedAllocations } from '../hooks/useGetUnapprovedAllocations';
import { formatWeekLabel } from '../utils/periodFormatting';
import {
  filterByType,
  groupByWeekThenPot,
  type AllocationTypeFilter,
  type UnapprovedPotGroup,
  type UnapprovedWeekGroup,
} from '../utils/groupUnapprovedByWeekThenPot';

const TYPE_FILTER_PARAM = 'allocType';
const TYPE_FILTER_TABS: ChipTab[] = [
  { label: 'All', value: 'all' },
  { label: 'Variations', value: 'variations' },
  { label: 'Labour items', value: 'labour' },
];

export function UnapprovedAllocationsWidget() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetUnapprovedAllocations();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsedPotIds, setCollapsedPotIds] = useState<Set<string>>(
    new Set()
  );

  const typeFilter = parseTypeFilter(searchParams.get(TYPE_FILTER_PARAM));

  const weeks = useMemo(() => {
    const allocations = data?.allocations ?? [];
    return groupByWeekThenPot(filterByType(allocations, typeFilter));
  }, [data, typeFilter]);

  const handleTypeFilterChange = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(TYPE_FILTER_PARAM, value);
      return next;
    });
  };

  const handleTogglePot = (potId: string) => {
    setCollapsedPotIds(prev => toggleSetMember(prev, potId));
  };

  const handleRowClick = (allocation: UnapprovedAllocationDto) => {
    navigate(buildAllocationDeepLink(allocation));
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const totalCount = data?.totalCount ?? 0;

  return (
    <Card
      data-testid="unapproved-allocations-widget"
      className="min-w-0 flex-1"
    >
      <CardContent className="flex flex-col gap-2 p-6">
        <div className="flex items-center justify-between">
          <p className="type-body-lg font-bold">Unapproved Allocations</p>
          <p
            className="type-body-lg font-bold text-content-secondary"
            data-testid="unapproved-allocations-total-count"
          >
            {totalCount}
          </p>
        </div>

        <ChipTabs
          tabs={TYPE_FILTER_TABS}
          value={typeFilter}
          onChange={handleTypeFilterChange}
          data-testid="unapproved-allocations-type-filter"
        />

        <div
          className="max-h-[420px] overflow-y-auto"
          data-testid="unapproved-allocations-list"
        >
          {weeks.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="flex flex-col gap-4">
              {weeks.map(week => (
                <WeekSection
                  key={`${week.periodYear}-${week.periodWeekNumber}`}
                  week={week}
                  collapsedPotIds={collapsedPotIds}
                  onTogglePot={handleTogglePot}
                  onRowClick={handleRowClick}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function parseTypeFilter(value: string | null): AllocationTypeFilter {
  if (value === 'variations' || value === 'labour') {
    return value;
  }
  return 'all';
}

function toggleSetMember(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

function buildAllocationDeepLink(allocation: UnapprovedAllocationDto): string {
  const potPath = routeBuilders.finance.potDetail(allocation.potId);
  return `${potPath}?panel=allocationDetails&id=${allocation.allocationId}&siteId=${allocation.siteId}&plotId=${allocation.plotId}`;
}

interface WeekSectionProps {
  week: UnapprovedWeekGroup;
  collapsedPotIds: Set<string>;
  onTogglePot: (potId: string) => void;
  onRowClick: (allocation: UnapprovedAllocationDto) => void;
}

function WeekSection({
  week,
  collapsedPotIds,
  onTogglePot,
  onRowClick,
}: WeekSectionProps) {
  const weekLabel = formatWeekLabel([
    {
      year: week.periodYear,
      weekNumber: week.periodWeekNumber,
      periodStart: week.weekCommencing,
      periodEnd: week.weekCommencing,
    },
  ]);
  const weekCommencingLabel = format(parseISO(week.weekCommencing), 'd MMM');

  return (
    <div
      data-testid={`unapproved-week-${week.periodYear}-${week.periodWeekNumber}`}
    >
      <div className="sticky top-0 z-[1] bg-surface-default py-0.5">
        <span className="type-caption font-semibold text-content-secondary">
          {weekLabel} · w/c {weekCommencingLabel}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {week.pots.map(pot => (
          <PotGroupSection
            key={pot.potId}
            pot={pot}
            collapsed={collapsedPotIds.has(pot.potId)}
            onToggle={() => onTogglePot(pot.potId)}
            onRowClick={onRowClick}
          />
        ))}
      </div>
    </div>
  );
}

interface PotGroupSectionProps {
  pot: UnapprovedPotGroup;
  collapsed: boolean;
  onToggle: () => void;
  onRowClick: (allocation: UnapprovedAllocationDto) => void;
}

function PotGroupSection({
  pot,
  collapsed,
  onToggle,
  onRowClick,
}: PotGroupSectionProps) {
  return (
    <div data-testid={`unapproved-pot-group-${pot.potId}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`unapproved-pot-group-toggle-${pot.potId}`}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-surface-raised"
      >
        <ExpandMoreIcon
          aria-hidden="true"
          className={`size-[18px] transition-transform duration-150 ${
            collapsed ? '-rotate-90' : 'rotate-0'
          }`}
        />
        <PotStatusBadge status={pot.potStatus} />
        {pot.isGang ? (
          <GroupsIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-content-secondary"
          />
        ) : (
          <PersonIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-content-secondary"
          />
        )}
        <span className="type-body-md min-w-0 flex-1 truncate font-semibold">
          {pot.claimantName}
        </span>
        <span className="type-caption text-content-secondary">
          {pot.unapprovedCount} unapproved
        </span>
        <span className="type-body-md min-w-20 text-right font-semibold">
          {formatCurrency(pot.subtotal)}
        </span>
      </button>

      <Collapse in={!collapsed} timeoutMs={250} unmountOnExit>
        <div className="pt-0.5">
          {pot.allocations.map((allocation, index) => (
            <AllocationRow
              key={allocation.allocationId}
              allocation={allocation}
              isLast={index === pot.allocations.length - 1}
              onClick={onRowClick}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
}

interface AllocationRowProps {
  allocation: UnapprovedAllocationDto;
  isLast: boolean;
  onClick: (allocation: UnapprovedAllocationDto) => void;
}

function AllocationRow({ allocation, isLast, onClick }: AllocationRowProps) {
  const waitAge = formatDistanceToNow(parseISO(allocation.bookedAt), {
    addSuffix: true,
  });
  const label = [
    allocation.uniqueReference,
    allocation.workTypeName || 'Unknown work type',
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <button
      type="button"
      onClick={() => onClick(allocation)}
      data-testid={`unapproved-allocation-row-${allocation.allocationId}`}
      className="block w-full text-left hover:bg-surface-raised"
    >
      <div className="pl-3">
        <TreeConnectorBox isLast={isLast}>
          <div className="flex min-h-8 items-center gap-2 py-1 pr-2">
            <span className="type-body-md min-w-0 flex-1 truncate">
              {label}
            </span>
            {allocation.resubmissionCount > 0 && (
              <Chip
                label={`resubmitted ×${allocation.resubmissionCount}`}
                size="sm"
                color="warning"
                variant="outlined"
                className="shrink-0 text-[0.625rem]"
              />
            )}
            <span className="type-caption min-w-18 shrink-0 text-right text-content-secondary">
              {waitAge}
            </span>
            <span className="type-body-md min-w-18 shrink-0 text-right font-semibold">
              {formatCurrency(allocation.amountClaimed)}
            </span>
          </div>
        </TreeConnectorBox>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div
      className="py-8 text-center"
      data-testid="unapproved-allocations-empty"
    >
      <p className="type-body-md text-content-secondary">
        Nothing awaiting approval
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <Card
      data-testid="unapproved-allocations-widget-loading"
      className="min-w-0 flex-1"
    >
      <CardContent className="p-6">
        <Skeleton width={220} height={24} className="mb-4" />
        <Skeleton width="100%" height={32} className="mb-4" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
